import { buildAssessmentStatusFilterOptions } from "./assessmentStatuses";
import { CALENDAR_UNASSIGNED_AUDITOR } from "./assessmentCalendarClientFilters";
import { resolveCalendarStatusForApi } from "./calendarDefaultStatusFilters";

export { buildDefaultAdminCalendarStatusFilters } from "./calendarDefaultStatusFilters";

const GXP_OPTIONS = [
  { value: "GAMP", label: "GAMP - Automated Manufacturing" },
  { value: "GCP", label: "GCP - Clinical" },
  { value: "GDP", label: "GDP - Distribution" },
  { value: "GLP", label: "GLP - Laboratory" },
  { value: "GMP", label: "GMP - Manufacturing" },
  { value: "GVP", label: "GVP - Pharmacovigilance" },
];

const ASSESSMENT_TYPE_OPTIONS = [
  { value: "Vendor Qualification Assessment", label: "Vendor Qualification Assessment" },
  { value: "In-Study Assessment", label: "In-Study Assessment" },
  { value: "Internal Assessment", label: "Internal Assessment" },
];

const MULTI_SPONSOR_OPTIONS = [
  { value: "Non-Multi-Sponsor", label: "Non-Multi-Sponsor" },
  { value: "Multi-Sponsor", label: "Multi-Sponsor" },
  { value: "Multi-Sponsor - Request Pending", label: "Multi-Sponsor - Request Pending" },
];

const REQUEST_TYPE_OPTIONS = [
  { value: "Initial Request", label: "Initial Request" },
  { value: "Joined Request", label: "Joined Request" },
  { value: "Request Pending", label: "Request Pending" },
];

const ONSITE_REMOTE_OPTIONS = [
  { value: "Onsite", label: "Onsite" },
  { value: "Remote", label: "Remote" },
];

function mapProviderOptions(providers = []) {
  return providers.map((item) => ({
    value: item.docId || item.provider_id || item.id,
    label: item.label || item.provider_name || item.name,
  }));
}

function mapSponsorOptions(sponsors = []) {
  return sponsors.map((item) => ({
    value: item.sponsor_id || item.id || item.docId,
    label: item.sponsor_name || item.label || item.name,
  }));
}

function mapScopeOptions(rfis = []) {
  return rfis.map((item) => ({
    value: item.rfi_id || item.id,
    label: item.rfi_name || item.label || item.name,
  }));
}

function mapAuditorOptions(auditors = []) {
  const named = [...auditors]
    .map((item) => ({
      value: item.vqa_auditor_id || item.auditor_id || item.id,
      label: item.auditor_name || item.name || item.label,
    }))
    .filter((item) => item.value && item.label);
  return [{ value: CALENDAR_UNASSIGNED_AUDITOR, label: CALENDAR_UNASSIGNED_AUDITOR }, ...named];
}

export function buildCalendarFilterSections({
  isAdmin,
  providers = [],
  sponsors = [],
  rfis = [],
  auditors = [],
}) {
  const shared = [
    { id: "Vendor", name: "Vendor", searchable: true, options: mapProviderOptions(providers) },
    { id: "Scope", name: "Scope", searchable: true, options: mapScopeOptions(rfis) },
    { id: "AssessmentType", name: "Assessment Type", options: ASSESSMENT_TYPE_OPTIONS },
    { id: "GxP", name: "GxP", options: GXP_OPTIONS },
    { id: "MultiSponsor", name: "Multi-Sponsor", options: MULTI_SPONSOR_OPTIONS },
    { id: "RequestType", name: "Request Type", options: REQUEST_TYPE_OPTIONS },
    { id: "OnsiteRemote", name: "Onsite / Remote", options: ONSITE_REMOTE_OPTIONS },
  ];

  if (!isAdmin) return shared;

  const statusOptions = buildAssessmentStatusFilterOptions().map((item) => ({
    value: item.statusID,
    label: item.label,
  }));

  return [
    { id: "Sponsor", name: "Sponsor", searchable: true, options: mapSponsorOptions(sponsors) },
    ...shared,
    { id: "Auditor", name: "Auditor", options: mapAuditorOptions(auditors) },
    { id: "Status", name: "Assessment Status", options: statusOptions },
  ];
}

export function mapOnsiteRemoteValues(values = []) {
  return values
    .map((value) => {
      const normalized = String(value || "").toLowerCase();
      if (normalized === "onsite" || normalized === "on-site") return "onsite";
      if (normalized === "remote") return "remote";
      return null;
    })
    .filter(Boolean);
}

export function mapCalendarFiltersToApi(selectedFilters = {}, { isAdmin = false } = {}) {
  const auditorSelection = selectedFilters.Auditor || [];
  return {
    sponsorIds: (selectedFilters.Sponsor || []).filter(Boolean),
    providerIds: (selectedFilters.Vendor || []).filter(Boolean),
    rfiIds: (selectedFilters.Scope || []).filter(Boolean),
    vqaStatus: resolveCalendarStatusForApi(selectedFilters, isAdmin),
    auditorIds: auditorSelection.filter((id) => id && id !== CALENDAR_UNASSIGNED_AUDITOR),
    includeUnassignedAuditor: auditorSelection.includes(CALENDAR_UNASSIGNED_AUDITOR),
    onsiteRemote: mapOnsiteRemoteValues(selectedFilters.OnsiteRemote || []),
    assessmentKind: (selectedFilters.AssessmentType || []).filter(Boolean),
    requestTypeList: (selectedFilters.RequestType || []).filter(Boolean),
    multiSponsorList: (selectedFilters.MultiSponsor || []).filter(Boolean),
    gxpList: (selectedFilters.GxP || []).filter(Boolean),
  };
}
